import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Fade,
  Zoom,
  TablePagination
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Inventory as PackageIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import packagesApiService from '../../services/packages/PackagesApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Packages = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [packagesData, setPackagesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [error, setError] = useState(null);
  const [packageSearchTerm, setPackageSearchTerm] = useState('');
  const [packageFilter, setPackageFilter] = useState('all');
  const [packageSortBy, setPackageSortBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchAgentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const agentData = await dashboardApiService.getAgentById(id);
      setAgent(agentData);
    } catch (err) {
      setError(`Failed to fetch agent details: ${err.message}`);
      console.error('Agent details fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchPackagesData = useCallback(async () => {
    try {
      setLoadingPackages(true);
      const packagesResponse = await packagesApiService.getAgentPackages(id);
      setPackagesData(packagesResponse);
    } catch (err) {
      console.error('Packages data fetch error:', err);
    } finally {
      setLoadingPackages(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAgentDetails();
      fetchPackagesData();
    }
  }, [id, fetchAgentDetails, fetchPackagesData]);

  const getFilteredAndSortedPackages = () => {
    if (!packagesData?.packages) return [];
    
    let filtered = packagesData.packages.filter(pkg => {
      const matchesSearch = pkg.name.toLowerCase().includes(packageSearchTerm.toLowerCase()) ||
                           pkg.description?.toLowerCase().includes(packageSearchTerm.toLowerCase());
      
      if (packageFilter === 'all') return matchesSearch;
      if (packageFilter === 'deb') return matchesSearch && pkg.format === 'deb';
      if (packageFilter === 'rpm') return matchesSearch && pkg.format === 'rpm';
      if (packageFilter === 'large') return matchesSearch && pkg.size > 1000000; // > 1MB
      
      return matchesSearch;
    });
    
    // Sort packages
    filtered.sort((a, b) => {
      switch (packageSortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'size':
          return (b.size || 0) - (a.size || 0);
        case 'version':
          return a.version.localeCompare(b.version);
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const getPackageIcon = (format, section) => {
    if (format === 'deb') return <PackageIcon sx={{ color: '#2196f3' }} />;
    if (section === 'admin') return <BuildIcon sx={{ color: '#ff9800' }} />;
    if (section === 'devel' || section === 'libdevel') return <CodeIcon sx={{ color: '#9c27b0' }} />;
    if (section === 'libs') return <MemoryIcon sx={{ color: '#4caf50' }} />;
    return <StorageIcon sx={{ color: '#607d8b' }} />;
  };

  const getPackageStats = () => {
    if (!packagesData?.packages) return { total: 0, deb: 0, rpm: 0, large: 0 };
    
    const packages = packagesData.packages;
    return {
      total: packages.length,
      deb: packages.filter(p => p.format === 'deb').length,
      rpm: packages.filter(p => p.format === 'rpm').length,
      large: packages.filter(p => p.size > 1000000).length
    };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const refreshData = () => {
    fetchAgentDetails();
    fetchPackagesData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading agent details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={refreshData}
          startIcon={<RefreshIcon />}
          sx={{ mr: 2 }}
        >
          Retry
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/agents/${id}`)}
          startIcon={<ArrowBackIcon />}
        >
          Back to Agent Details
        </Button>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Agent not found</Typography>
        <Typography variant="body2">The requested agent could not be found.</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Agents
        </Button>
      </Box>
    );
  }

  const filteredPackages = getFilteredAndSortedPackages();
  const paginatedPackages = filteredPackages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const stats = getPackageStats();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Breadcrumbs>
          <Link 
            color="inherit" 
            onClick={() => navigate('/agents')}
            sx={{ cursor: 'pointer' }}
          >
            Agents
          </Link>
          <Link 
            color="inherit" 
            onClick={() => navigate(`/agents/${id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {agent.name}
          </Link>
          <Typography color="text.primary">Packages</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={() => navigate(`/agents/${id}`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton 
            onClick={refreshData}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Agent Info Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <ComputerIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Package Inventory - {agent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agent ID: {agent.id} • {agent.ip} • {agent.os.name} {agent.os.version}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${stats.total} Total`} color="primary" />
              <Chip label={`${stats.deb} DEB`} sx={{ bgcolor: '#e3f2fd' }} />
              {stats.rpm > 0 && <Chip label={`${stats.rpm} RPM`} sx={{ bgcolor: '#fff3e0' }} />}
              <Chip label={`${stats.large} Large`} sx={{ bgcolor: '#f3e5f5' }} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search packages..."
                value={packageSearchTerm}
                onChange={(e) => setPackageSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter</InputLabel>
                <Select
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  label="Filter"
                  startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="all">All Packages</MenuItem>
                  <MenuItem value="deb">DEB Packages</MenuItem>
                  <MenuItem value="rpm">RPM Packages</MenuItem>
                  <MenuItem value="large">Large Packages (&gt;1MB)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={packageSortBy}
                  onChange={(e) => setPackageSortBy(e.target.value)}
                  label="Sort By"
                  startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="size">Size</MenuItem>
                  <MenuItem value="version">Version</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {filteredPackages.length} packages found
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Packages Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loadingPackages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading packages...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Package</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Architecture</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPackages.map((pkg, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getPackageIcon(pkg.format, pkg.section)}
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {pkg.name}
                              </Typography>
                              <Chip 
                                label={pkg.format?.toUpperCase() || 'Unknown'}
                                size="small"
                                sx={{ 
                                  fontSize: '10px', 
                                  height: 18,
                                  bgcolor: pkg.format === 'deb' ? '#e3f2fd' : '#fff3e0'
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {pkg.version}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {pkg.architecture || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(pkg.size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {pkg.section || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={pkg.description}
                          >
                            {pkg.description || 'No description available'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredPackages.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Packages;
